/**
 * Нога Polymarket по рынку: сумма в долларах, исполнение по нашему стакану.
 *
 * Лимитка «N шаров по худшему уровню» и рыночная покупка «на X $» при неподвижной
 * книге берут одно и то же. Разница — в том, что остаётся, когда книга уехала:
 * лимитка оставляет недолив висеть в стакане (у спорта его снимают на старте
 * матча, в лайве он может налиться потом, когда контора уже стоит), а рыночный
 * ордер сайта — FAK, остаток отменяется сразу. Работают «по рынку» все, и
 * владелец тоже (голосовые 01.10: «полностью уйти от работы с лимитками»).
 *
 * Как сайт исполняет рыночную покупку — снято с бандла polymarket.com 02.10.2026:
 *  - поле `shares` купона в режиме Market — это ДОЛЛАРЫ, не шары;
 *  - потолок цены = цена уровня, на котором накопленная стоимость asks
 *    достигает суммы, × (1 + допуск/100), но не выше 1 − шаг (`calculateWorstBuyPrice`);
 *  - допуск по умолчанию 1.5 % (`DEFAULT_SLIPPAGE_LIMIT`), человек меняет его
 *    в настройках сайта (cookie `slippage_tolerance`);
 *  - тип ордера по умолчанию FAK (`LS_ORDER_TYPE`): нальётся, сколько есть;
 *  - комиссия taker удерживается шарами.
 *
 * Отсюда три вопроса, на которые карточка отвечает до нажатия:
 *  1. сколько шаров придёт на эту сумму по нашему стакану и по какой средней;
 *  2. до какой суммы вилка ещё в плюсе при этой книге — «пройдёт / не пройдёт»;
 *  3. не пустит ли допуск сайта цену выше той, где вилки уже нет.
 *
 * Только `Decimal`. Зеркало — `backend/forq/poly_market.py`, контрольные примеры
 * в `market.test.ts` и `backend/tests/test_poly_market.py` совпадают до цента.
 */

import { Decimal } from 'decimal.js';

import { type BookLevel, type FeeModel, legCostFraction, SPORTS_FEE_RATE, walkBook } from '../lib/calc';

export type PolyOrder = 'market' | 'limit';

export const asPolyOrder = (value: unknown, fallback: PolyOrder = 'limit'): PolyOrder =>
  value === 'market' || value === 'limit' ? value : fallback;

/** Допуск проскальзывания сайта по умолчанию, проценты. */
export const SITE_SLIPPAGE = '1.5';

export interface BookFee {
  rate?: string;
  exponent?: number;
  model?: FeeModel;
}

export interface MarketFill {
  /** Сколько долларов стакан примет из запрошенных. */
  spent: Decimal;
  /** Куплено шаров до комиссии. */
  shares: Decimal;
  /** Останется на руках после удержания комиссии. Это и есть выплата ноги. */
  net: Decimal;
  avg: Decimal | null;
  /** Худший задетый уровень. */
  worst: Decimal | null;
  /** Стакан кончился раньше суммы. */
  short: boolean;
}

const ZERO = new Decimal(0);
const ONE = new Decimal(1);

const costOf = (price: Decimal, fee: BookFee): Decimal =>
  legCostFraction(price, fee.rate ?? SPORTS_FEE_RATE, fee.exponent ?? 1, fee.model);

/** Рыночная покупка на `usd` по уровням `asks` (цена, шары; от лучшей к худшей). */
export function marketBuy(asks: readonly BookLevel[], usd: Decimal.Value, fee: BookFee = {}): MarketFill {
  const want = new Decimal(usd);
  if (asks.length === 0 || !want.gt(0)) {
    return { spent: ZERO, shares: ZERO, net: ZERO, avg: null, worst: null, short: asks.length === 0 };
  }
  const walked = walkBook(
    {
      kind: 'book',
      asks,
      feeRate: fee.rate ?? SPORTS_FEE_RATE,
      feeExponent: fee.exponent ?? 1,
      ...(fee.model ? { feeModel: fee.model } : {}),
    },
    want,
  );
  const shares = walked.shares ?? ZERO;
  return {
    spent: walked.stake,
    shares,
    net: shares.times(ONE.minus(walked.costFraction)),
    avg: walked.avgPrice,
    worst: walked.limitPrice,
    short: walked.stake.lt(want),
  };
}

/**
 * Какой кэф после комиссии нужен бирже, чтобы вилка дала `minRoi` при кэфе
 * второй ноги `other`. `null` — недостижимо при любой цене.
 *
 * ROI вилки = 1 / (1/E + 1/k) − 1, отсюда 1/E ≤ 1/(1 + minRoi) − 1/k.
 */
function neededOdds(other: Decimal, minRoi: Decimal): Decimal | null {
  if (!other.gt(1)) return null;
  const room = ONE.div(ONE.plus(minRoi)).minus(ONE.div(other));
  return room.gt(0) ? ONE.div(room) : null;
}

export interface MarketRoom {
  /** До скольких долларов вилка держит `minRoi` при этой книге. */
  usd: Decimal;
  net: Decimal;
  worst: Decimal | null;
  /** Во что упёрлись: в цену (`fork`) или в конец стакана (`book`). */
  by: 'fork' | 'book';
}

/**
 * Сколько можно купить по рынку, пока вся вилка остаётся в плюсе.
 *
 * Идём по уровням и держим среднюю: каждый уровень дороже, и средняя растёт.
 * Уровень берётся целиком, пока отношение «шары после комиссии / доллары» не
 * ниже нужного; на уровне, где оно падает ниже, берём ровно ту часть, при которой
 * средняя упирается в границу: (N + x·n) = E·(S + x·p) → x = (N − E·S) / (E·p − n).
 */
export function marketRoom(
  asks: readonly BookLevel[],
  otherOdds: Decimal.Value,
  fee: BookFee = {},
  minRoi: Decimal.Value = 0,
): MarketRoom {
  const need = neededOdds(new Decimal(otherOdds), new Decimal(minRoi));
  let net = ZERO;
  let spent = ZERO;
  let worst: Decimal | null = null;
  if (need === null) return { usd: ZERO, net, worst, by: 'fork' };
  for (const [rawPrice, rawSize] of asks) {
    const price = new Decimal(rawPrice);
    const size = new Decimal(rawSize);
    if (!price.gt(0) || !price.lt(1) || !size.gt(0)) continue;
    const keep = ONE.minus(costOf(price, fee));
    const whole = net.plus(size.times(keep));
    if (whole.gte(need.times(spent.plus(size.times(price))))) {
      net = whole;
      spent = spent.plus(size.times(price));
      worst = price;
      continue;
    }
    const part = Decimal.max(ZERO, Decimal.min(size, net.minus(need.times(spent)).div(need.times(price).minus(keep))));
    if (part.gt(0)) {
      net = net.plus(part.times(keep));
      spent = spent.plus(part.times(price));
      worst = price;
    }
    return { usd: spent, net, worst, by: 'fork' };
  }
  return { usd: spent, net, worst, by: 'book' };
}

/**
 * Цена шара, при которой вилки нет: всё куплено по ней — и ROI ровно ноль.
 *
 * Решается делением отрезка: комиссия зависит от цены нелинейно, а у Predict.fun
 * своя кривая. Сорок шагов дают точность много ниже тика.
 */
export function deadPrice(otherOdds: Decimal.Value, fee: BookFee = {}, minRoi: Decimal.Value = 0): Decimal | null {
  const need = neededOdds(new Decimal(otherOdds), new Decimal(minRoi));
  if (need === null) return null;
  const pays = (p: Decimal): boolean => ONE.minus(costOf(p, fee)).div(p).gte(need);
  let low = new Decimal('0.0001');
  let high = new Decimal('0.9999');
  if (!pays(low)) return null;
  if (pays(high)) return high;
  for (let i = 0; i < 40; i += 1) {
    const mid = low.plus(high).div(2);
    if (pays(mid)) low = mid;
    else high = mid;
  }
  return low.toDecimalPlaces(4, Decimal.ROUND_DOWN);
}

/**
 * Сколько долларов лежит в asks не дороже `price`. Это «под наш кэф»:
 * рыночная покупка сначала съест эти уровни.
 */
export function usdAtPrice(asks: readonly BookLevel[], price: Decimal.Value): Decimal {
  const cap = new Decimal(price);
  if (!cap.gt(0)) return ZERO;
  let sum = ZERO;
  for (const [rawPrice, rawSize] of asks) {
    const px = new Decimal(rawPrice);
    const size = new Decimal(rawSize);
    if (px.gt(0) && px.lte(cap) && size.gt(0)) sum = sum.plus(px.times(size));
  }
  return sum;
}

export interface BookRung {
  price: Decimal;
  shares: Decimal;
  usd: Decimal;
  cum: Decimal;
  /** Этот уровень ещё ест запланированная сумма. */
  take: boolean;
}

/** Лестница asks: цена, шары, доллары уровня и накопленное. */
export function paintAsks(asks: readonly BookLevel[], usd: Decimal.Value, max = 8): BookRung[] {
  const want = new Decimal(usd);
  let cum = ZERO;
  const out: BookRung[] = [];
  for (const [rawPrice, rawSize] of asks) {
    const price = new Decimal(rawPrice);
    const shares = new Decimal(rawSize);
    if (!price.gt(0) || !shares.gt(0)) continue;
    const level = price.times(shares);
    const before = cum;
    cum = cum.plus(level);
    out.push({ price, shares, usd: level, cum, take: want.gt(0) && before.lt(want) });
    if (out.length >= max) break;
  }
  return out;
}

/** Уровень, на котором накопленная стоимость asks достигает суммы. `null` — не достигает. */
function levelFor(asks: readonly BookLevel[], usd: Decimal): Decimal | null {
  let value = ZERO;
  for (const [rawPrice, rawSize] of asks) {
    const price = new Decimal(rawPrice);
    value = value.plus(price.times(rawSize));
    if (value.gte(usd)) return price;
  }
  return null;
}

/** Потолок цены, который сайт Polymarket поставит на рыночную покупку `usd`. */
export function siteCap(
  asks: readonly BookLevel[],
  usd: Decimal.Value,
  slippage: Decimal.Value = SITE_SLIPPAGE,
  tick: Decimal.Value = '0.01',
): Decimal | null {
  const level = levelFor(asks, new Decimal(usd));
  if (level === null) return null;
  const capped = level.times(ONE.plus(new Decimal(slippage).div(100)));
  return Decimal.min(capped, ONE.minus(tick));
}

/** Самый большой допуск сайта, при котором его потолок не выше `dead`. Вниз к 0.1 %. */
export function safeSlippage(asks: readonly BookLevel[], usd: Decimal.Value, dead: Decimal.Value): Decimal | null {
  const level = levelFor(asks, new Decimal(usd));
  if (level === null) return null;
  const room = new Decimal(dead).div(level).minus(1).times(100);
  return Decimal.max(ZERO, room.toDecimalPlaces(1, Decimal.ROUND_DOWN));
}

export type MarketState = 'ok' | 'minus' | 'thin' | 'empty';

export interface MarketCheck {
  state: MarketState;
  fill: MarketFill;
  room: MarketRoom | null;
  dead: Decimal | null;
  cap: Decimal | null;
  safe: Decimal | null;
  /** Одна строка для плитки: пройдёт или нет и почему. */
  say: string;
  /** Допуск сайта пускает цену, где вилки нет. `null` — не пускает или не знаем. */
  warn: string | null;
}

export interface MarketAsk {
  asks: readonly BookLevel[];
  usd: Decimal.Value;
  /** Кэф второй ноги после её комиссий: её выплата, делённая на ставку. */
  otherOdds: Decimal.Value;
  fee?: BookFee;
  tick?: Decimal.Value;
  slippage?: Decimal.Value;
}

const money = (value: Decimal): string => value.toDecimalPlaces(2, Decimal.ROUND_DOWN).toFixed(2);
const cents = (value: Decimal): string => value.times(100).toDecimalPlaces(1, Decimal.ROUND_HALF_UP).toString();
const shares = (value: Decimal): string => value.toDecimalPlaces(1, Decimal.ROUND_DOWN).toFixed(1);

/** Пройдёт ли рыночная покупка на эту сумму по нашему стакану. */
export function checkMarket(ask: MarketAsk): MarketCheck {
  const fee = ask.fee ?? {};
  const usd = new Decimal(ask.usd);
  const fill = marketBuy(ask.asks, usd, fee);
  if (ask.asks.length === 0) {
    return {
      state: 'empty', fill, room: null, dead: null, cap: null, safe: null,
      say: 'стакана нет — по рынку вслепую', warn: null,
    };
  }
  const room = marketRoom(ask.asks, ask.otherOdds, fee);
  const dead = deadPrice(ask.otherOdds, fee);
  const slippage = ask.slippage ?? SITE_SLIPPAGE;
  const cap = siteCap(ask.asks, usd, slippage, ask.tick ?? '0.01');
  const safe = dead === null ? null : safeSlippage(ask.asks, usd, dead);
  // Потолок выше цены смерти значит: сдвинься книга до нажатия — сайт нальёт в
  // минус. Если вниз допуском не уйти (сам уровень уже дороже), честный совет —
  // лимитка, а не «0 %», который от сдвига не спасает.
  const warn = cap === null || dead === null || !cap.gt(dead) || safe === null
    ? null
    : safe.gt(0)
      ? `потолок сайта ${cents(cap)} ¢ при допуске ${new Decimal(slippage).toString()} %, вилки нет с ${cents(dead)} ¢ — поставьте допуск не больше ${safe.toString()} %`
      : `потолок сайта ${cents(cap)} ¢, вилки нет с ${cents(dead)} ¢: сдвинется книга — нальёт в минус; надёжнее лимиткой`;
  if (fill.short) {
    return {
      state: 'thin', fill, room, dead, cap, safe, warn,
      say: `в стакане только ${money(fill.spent)} $ — остальное не нальётся`,
    };
  }
  const avg = fill.avg === null ? '' : `, средняя ${cents(fill.avg)} ¢`;
  if (room.usd.lt(usd.minus('0.005'))) {
    return {
      state: 'minus', fill, room, dead, cap, safe, warn,
      say: room.usd.gt(0)
        ? `не пройдёт: на ${money(usd)} $${avg} — вилка в минусе; в плюс входит до ${money(room.usd)} $`
        : `не пройдёт: по стакану вилки уже нет${avg}`,
    };
  }
  const tail = room.by === 'book' ? `стакан держит до ${money(room.usd)} $` : `плюс держится до ${money(room.usd)} $`;
  return {
    state: 'ok', fill, room, dead, cap, safe, warn,
    say: `пройдёт: ${money(usd)} $ → ${shares(fill.net)} шер${avg}; ${tail}`,
  };
}
